import { lazy, Suspense } from "react";
import { Navigate, Outlet, useLocation } from "react-router";
import { authError } from "@/api/auth";
import { useMe } from "@/api/auth";
import { Pending } from "./Pending";

// Lazy: the error screens stay out of the first load.
const SessionError = lazy(() => import("./SessionError"));
// The account's locale follows the app's language (card text comes localized from the server).
const LocaleSync = lazy(() => import("@/features/settings/LocaleSync"));
// The signed-out home: the welcome page (the game took over the apex from the beta landing).
const WelcomePage = lazy(() => import("@/features/growth/launch/WelcomePage"));

/** Lets signed-in players through; shows the welcome page on `/` to everyone else, and sends them to sign-in from
 * any other page, remembering where they were going. */
export function RequireAuth() {
  const location = useLocation();
  const me = useMe();
  if (me.isPending) return <Pending />;
  if (me.isError)
    return (
      <Suspense fallback={<Pending />}>
        <SessionError error={me.error} onRetry={() => void me.refetch()} />
      </Suspense>
    );
  if (me.data === null) {
    // A failed sign-in comes back as /?auth_error=<code>: show it on the sign-in screen.
    const failure = authError(new URLSearchParams(location.search));
    if (failure) return <Navigate to={`/sign-in?auth_error=${failure}`} replace />;
    if (location.pathname === "/")
      return (
        <Suspense fallback={<Pending />}>
          <WelcomePage />
        </Suspense>
      );
    const returnTo = location.pathname + location.search + location.hash;
    return <Navigate to={`/sign-in?return_to=${encodeURIComponent(returnTo)}`} replace />;
  }
  return (
    <>
      <Outlet />
      <Suspense fallback={null}>
        <LocaleSync locale={me.data.locale} />
      </Suspense>
    </>
  );
}
